/**
 * Build inbox application candidates for a log row (shared: UI + auto-confirm gate).
 * @see docs/APP_UI_SPEC.md — D1, D4
 */

import { extractInboundEmail } from './inboundEmail.js';
import { resolveOfferDateForInboxRow } from './bookingEmail.js';
import { APPLICATION_STATUSES } from './applicationStatus.js';

export const INBOX_APP_SELECT =
  'id, email, status, comedian_name, event_id, events(id, event_date, event_name, venue_id, venues(venue_name, venue_slug))';

export function normalizeInboxSender(row) {
  return (
    extractInboundEmail(row.sender_email || '') ||
    (row.sender_email || '').trim().toLowerCase()
  );
}

export function displayCandidateFromApplication(app, flags = {}) {
  const suggestedByLlm = Boolean(flags.suggestedByLlm);
  const matchesEmailOfferDate = Boolean(flags.matchesEmailOfferDate);
  const ev = app.events;
  return {
    id: app.id,
    email: app.email,
    status: app.status || APPLICATION_STATUSES.PENDING,
    comedian_name: app.comedian_name,
    suggestedByLlm,
    matchesEmailOfferDate,
    event: ev
      ? {
          id: ev.id,
          event_date: ev.event_date,
          event_name: ev.event_name,
          venue_id: ev.venue_id,
          venue_name: ev.venues?.venue_name,
          venue_slug: ev.venues?.venue_slug,
        }
      : null,
  };
}

/**
 * @param {object} row - `email_inbox_log` row
 * @param {Map<number, object>} appsById
 * @param {Map<string, object[]>} allAppsByEmail - keys lowercased email
 * @returns {ReturnType<typeof displayCandidateFromApplication>[]}
 */
export function buildInboxCandidatesForRow(row, appsById, allAppsByEmail) {
  const offerDate = resolveOfferDateForInboxRow(row);
  const ids = row.application_ids || [];
  const senderKey = normalizeInboxSender(row).toLowerCase();
  const allForSender = allAppsByEmail.get(senderKey) || [];
  const offeredForSender = allForSender.filter((a) => a.status === APPLICATION_STATUSES.OFFERED);
  const seen = new Set();
  const list = [];

  const pushApp = (app, flags) => {
    if (!app || seen.has(app.id)) return;
    seen.add(app.id);
    list.push(displayCandidateFromApplication(app, flags));
  };

  if (offerDate) {
    for (const app of allForSender) {
      if (app.events?.event_date === offerDate) {
        pushApp(app, {
          suggestedByLlm: ids.includes(app.id),
          matchesEmailOfferDate: true,
        });
      }
    }
  }

  for (const id of ids) {
    const app = appsById.get(id);
    if (!app) continue;
    pushApp(app, {
      suggestedByLlm: true,
      matchesEmailOfferDate: Boolean(offerDate && app.events?.event_date === offerDate),
    });
  }

  for (const app of offeredForSender) {
    pushApp(app, {
      suggestedByLlm: ids.includes(app.id),
      matchesEmailOfferDate: Boolean(offerDate && app.events?.event_date === offerDate),
    });
  }

  list.sort((a, b) => {
    if (a.matchesEmailOfferDate !== b.matchesEmailOfferDate) {
      return a.matchesEmailOfferDate ? -1 : 1;
    }
    const order = {
      [APPLICATION_STATUSES.OFFERED]: 0,
      [APPLICATION_STATUSES.PENDING]: 1,
      [APPLICATION_STATUSES.CONFIRMED]: 2,
      [APPLICATION_STATUSES.REJECTED]: 3,
    };
    const oa = order[a.status] ?? 9;
    const ob = order[b.status] ?? 9;
    if (oa !== ob) return oa - ob;
    if (a.suggestedByLlm !== b.suggestedByLlm) return a.suggestedByLlm ? -1 : 1;
    return a.id - b.id;
  });

  return list;
}
